import React, { useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Select,
  MenuItem,
  InputAdornment,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Box,
  Checkbox,
  ListItemText,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from "react-hook-form";
import { useQuery } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";

// Location-specific fields (all required) - updated labels to lowercase
const locationFields = [
  { name: "currency", label: "currency", type: "select" },
  { name: "price", label: "price", type: "number" },
  { name: "sku", label: "sku", type: "text" },
  { name: "stockOnHand", label: "stock on hand", type: "number" },
  { name: "stockCommitted", label: "stock committed", type: "number" },
  {
    name: "awaitingNewStockDelivery",
    label: "awaiting new stock delivery",
    type: "date",
  },
  {
    name: "awaitingNewStockCount",
    label: "awaiting new stock count",
    type: "number",
  },
];

// Transform locationValues to locationStock using locationId (from locationsListOptions)
function transformLocationValuesWithId(
  locationValues = {},
  locationsListOptions = []
) {
  return Object.entries(locationValues).map(([locationKey, values]) => {
    const locationObj = locationsListOptions.find(
      (loc) => loc.value === locationKey
    );
    return {
      locationId: locationObj ? locationObj.id : locationKey,
      ...values,
    };
  });
}

export default function VariantDialog({
  open,
  onClose,
  onSave,
  defaultValues = {},
  locationsListOptions = [],
  isCatalog = false,
  businessLocation = null,
  baseItemCode = "",
}) {
  // Currency query
  const { data: currencyData } = useQuery(
    ["currency"],
    async ({ signal }) => {
      const res = await axiosInstance.get(ApiEndPoints.COMMON.currency, {
        signal,
      });
      return res.data.data;
    },
    { refetchOnWindowFocus: false, cacheTime: 30 * 60 * 1000 }
  );

  // Transform API currency data to match expected format
  const currencies = useMemo(() => {
    if (!currencyData?.currencies) {
      // Fallback to default currencies if API data is not available
      return [
        { code: "USD", label: "united states (usd) $", symbol: "$" },
        { code: "EUR", label: "euro (eur) €", symbol: "€" },
        { code: "GBP", label: "british pound (gbp) £", symbol: "£" },
        { code: "JPY", label: "japanese yen (jpy) ¥", symbol: "¥" },
        { code: "CAD", label: "canadian dollar (cad) $", symbol: "$" },
        { code: "CNY", label: "chinese yuan (cny) ¥", symbol: "¥" },
        { code: "AUD", label: "australian dollar (aud) $", symbol: "$" },
        { code: "CHF", label: "swiss franc (chf) fr", symbol: "Fr" },
        { code: "HKD", label: "hong kong dollar (hkd) $", symbol: "$" },
        { code: "INR", label: "indian rupee (inr) ₹", symbol: "₹" },
      ];
    }

    // Get unique currencies (group by currency code)
    const uniqueCurrencies = new Map();
    currencyData.currencies.forEach((item) => {
      const currencyCode = item.currency;
      if (!uniqueCurrencies.has(currencyCode)) {
        const symbol = item.symbol || currencyCode;
        uniqueCurrencies.set(currencyCode, {
          code: currencyCode,
          label: `${item.name.toLowerCase()} (${currencyCode.toLowerCase()}) ${symbol}`,
          symbol: symbol,
          iso2: item.iso2, // Store iso2 for country matching
        });
      }
    });

    // Convert map to array and sort by currency code
    return Array.from(uniqueCurrencies.values()).sort((a, b) =>
      a.code.localeCompare(b.code)
    );
  }, [currencyData]);

  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      variantDescription: "",
      totalWeight: "",
      kcal: "",
      protein: "",
      carbs: "",
      fats: "",
      fibre: "",
      variant: "",
      variantNumber: "",
      stockOnHand: "", // Changed from qty to stockOnHand
      locations: [],
      locationValues: {},
      // Catalog-specific fields removed - stock is now location-based
      ...defaultValues,
    },
  });

  // Reset when the dialog opens/closes or defaultValues change
  useEffect(() => {
    if (open) {
      const newDefaults = {
        variantDescription: "",
        totalWeight: "",
        kcal: "",
        protein: "",
        carbs: "",
        fats: "",
        fibre: "",
        variant: "",
        variantNumber: "",
        stockOnHand: "", // Changed from qty to stockOnHand
        locations: [],
        locationValues: {},
        // Catalog-specific fields removed - stock is now location-based
        ...defaultValues,
      };
 
      reset(newDefaults);

      // Set default currency based on location after reset (only for new variants)
      // Check if it's a new variant: no locations or no currency in locationValues
      const hasExistingLocations = defaultValues.locations && defaultValues.locations.length > 0;
      const hasExistingCurrency = hasExistingLocations && 
        defaultValues.locationValues && 
        Object.values(defaultValues.locationValues).some(loc => loc.currency);
      const isNewVariant = !hasExistingCurrency;
      
      if (isNewVariant && currencyData?.currencies && currencies.length) {
        const setDefaultCurrencyByCountry = (countryCode) => {
          if (!countryCode || !currencyData?.currencies) {
            console.log("No country code or currency data available");
            return;
          }

          // Find currency for the country - try both uppercase and lowercase
          const countryCurrency = currencyData.currencies.find(
            (item) => item.iso2?.toUpperCase() === countryCode.toUpperCase()
          );

          if (countryCurrency) {
            console.log("Found currency for country:", countryCode, countryCurrency.currency);
            // Set default currency for all selected locations - use longer timeout to ensure form is reset
            setTimeout(() => {
              const currentLocations = watch("locations") || [];
              if (currentLocations.length > 0) {
                const currentLocationValues = watch("locationValues") || {};
                const updatedLocationValues = { ...currentLocationValues };
                currentLocations.forEach(locValue => {
                  if (!updatedLocationValues[locValue]) {
                    updatedLocationValues[locValue] = {};
                  }
                  if (!updatedLocationValues[locValue].currency) {
                    updatedLocationValues[locValue].currency = countryCurrency.currency;
                  }
                });
                setValue("locationValues", updatedLocationValues, { shouldDirty: false });
              }
            }, 300);
          } else {
            console.log("No currency found for country code:", countryCode);
          }
        };

        const getCountryFromIP = async () => {
          try {
            console.log("Getting country from IP...");
            const response = await fetch(
              process.env.REACT_APP_LOCATION_URL || "https://ipapi.co/json/"
            );
            const data = await response.json();
            console.log("IP location data:", data);
            const countryCode = data?.country_code || data?.country_code_iso2;
            if (countryCode) {
              console.log("Country code from IP:", countryCode);
              setDefaultCurrencyByCountry(countryCode);
            } else {
              console.log("No country code found in IP response");
            }
          } catch (error) {
            console.error("Error getting country from IP:", error);
          }
        };

        const getCountryAndSetCurrency = async () => {
          try {
            console.log("Starting location detection for currency...");
            // Try to get location from geolocation API first
            if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(
                async (position) => {
                  try {
                    console.log("Got geolocation:", position.coords);
                    // Use reverse geocoding API to get country from lat/long
                    const response = await fetch(
                      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
                    );
                    const data = await response.json();
                    console.log("Reverse geocode data:", data);
                    const countryCode = data?.countryCode;
                    if (countryCode) {
                      console.log("Country code from geolocation:", countryCode);
                      setDefaultCurrencyByCountry(countryCode);
                    } else {
                      console.log("No country code from geolocation, falling back to IP");
                      // Fallback to IP-based location
                      getCountryFromIP();
                    }
                  } catch (error) {
                    console.error("Error in reverse geocoding:", error);
                    // Fallback to IP-based location
                    getCountryFromIP();
                  }
                },
                async (error) => {
                  console.log("Geolocation error, falling back to IP:", error);
                  // Fallback to IP-based location
                  getCountryFromIP();
                }
              );
            } else {
              console.log("Geolocation not available, using IP");
              // Fallback to IP-based location
              getCountryFromIP();
            }
          } catch (error) {
            console.error("Error getting location:", error);
          }
        };

        getCountryAndSetCurrency();
      }
    }
  }, [open, defaultValues, reset, currencyData, currencies, watch, setValue]);

  const selectedLocations = watch("locations");
  const locationValues = watch("locationValues");

  // Helper function to get country code from lat/long using reverse geocode API
  const getCountryCodeFromCoordinates = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      );
      const data = await response.json();
      return data?.countryCode || null;
    } catch (error) {
      console.error("Error getting country code from coordinates:", error);
      return null;
    }
  };

  // Helper function to get default currency from country code
  const getDefaultCurrencyFromCountryCode = async (countryCode) => {
    if (!countryCode) return "USD";
    try {
      const response = await axiosInstance.get(
        ApiEndPoints.BANK_ACCOUNT.getCountryFields(countryCode)
      );
      const countryFields = response?.data?.data;
      // Assuming the API returns a currency field or defaultCurrency field
      return countryFields?.currency || countryFields?.defaultCurrency || "USD";
    } catch (error) {
      console.error("Error getting default currency from country code:", error);
      return "USD";
    }
  };

  // Helper function to set default currency for selected locations
  const setDefaultCurrencyForLocations = async (selectedLocationValues) => {
    if (!selectedLocationValues?.length) return;

    const currentLocationValues = watch("locationValues") || {};
    const updatedLocationValues = { ...currentLocationValues };

    // Get previous locations to find newly selected ones
    const previousLocationValues = watch("locations") || [];
    const newlySelected = selectedLocationValues.filter(
      (loc) => !previousLocationValues.includes(loc)
    );

    // First, set default USD immediately for all newly selected locations
    newlySelected.forEach((locValue) => {
      if (!updatedLocationValues[locValue]) {
        updatedLocationValues[locValue] = {};
      }
      // Ensure currency is always set (default to USD if not set)
      if (!updatedLocationValues[locValue].currency) {
        updatedLocationValues[locValue].currency = "USD";
      }
    });

    // Also ensure all selected locations have currency set
    selectedLocationValues.forEach((locValue) => {
      if (!updatedLocationValues[locValue]) {
        updatedLocationValues[locValue] = {};
      }
      if (!updatedLocationValues[locValue].currency) {
        updatedLocationValues[locValue].currency = "USD";
      }
    });

    // Update form immediately with default USD
    setValue("locationValues", updatedLocationValues);

    // Then asynchronously update with location-specific currency (if businessLocation available)
    if (businessLocation?.locations) {
      // Process all selected locations that need currency updates
      // This includes newly selected locations and existing ones that don't have currency set or have USD
      const locationsToProcess = selectedLocationValues.filter((locValue) => {
        const currentCurrency = updatedLocationValues[locValue]?.currency;
        // Process if: newly selected OR currency not set OR currency is USD (might need update)
        return newlySelected.includes(locValue) || !currentCurrency || currentCurrency === "USD";
      });
      
      console.log("VariantDialog - Locations to process for currency:", locationsToProcess);
      
      // Process all locations that need currency updates and batch updates
      const currencyUpdates = {};
      
      for (const locValue of locationsToProcess) {
        // Find location object by value
        const locObj = locationsListOptions.find((loc) => loc.value === locValue);
        
        // Find full location data from businessLocation
        // Try matching by ID first, then fallback to matching by name
        let fullLocation = null;
        
        if (locObj) {
          fullLocation = businessLocation.locations.find(
            (loc) => loc._id === locObj.id
          );
        }
        
        // Fallback: try matching directly by businessLocationName
        if (!fullLocation) {
          fullLocation = businessLocation.locations.find(
            (loc) => loc.businessLocationName === locValue
          );
        }
        
        if (!fullLocation) {
          console.warn(`VariantDialog - Full location not found for value: ${locValue}`, {
            locObj,
            availableLocations: businessLocation.locations.map(l => ({
              _id: l._id,
              name: l.businessLocationName
            }))
          });
          continue;
        }
        
        if (!fullLocation.location?.coordinates) {
          console.warn(`VariantDialog - No coordinates found for location: ${locValue}`, {
            fullLocation: {
              _id: fullLocation._id,
              name: fullLocation.businessLocationName,
              hasLocation: !!fullLocation.location,
              locationKeys: fullLocation.location ? Object.keys(fullLocation.location) : []
            }
          });
          continue;
        }

        // Extract coordinates (GeoJSON format: [longitude, latitude])
        const [longitude, latitude] = fullLocation.location.coordinates;
        
        console.log(`VariantDialog - Found coordinates for ${locValue}:`, { longitude, latitude });
        
        if (!longitude || !latitude) {
          console.warn(`VariantDialog - Invalid coordinates for location: ${locValue}`, { longitude, latitude });
          continue;
        }
        
        // Get country code from coordinates
        const countryCode = await getCountryCodeFromCoordinates(latitude, longitude);
      
        if (!countryCode) {
          console.warn(`VariantDialog - No country code found for coordinates: ${latitude}, ${longitude}`);
          continue;
        }

        // Get default currency from country code
        const defaultCurrency = await getDefaultCurrencyFromCountryCode(countryCode);
        
        console.log(`VariantDialog - Currency for ${locValue}:`, { countryCode, defaultCurrency });
        
        // Store currency update for batching (update if we got a valid currency)
        if (defaultCurrency) {
          currencyUpdates[locValue] = defaultCurrency;
        }
      }

      // Batch update all currency changes at once
      if (Object.keys(currencyUpdates).length > 0) {
        const currentValues = watch("locationValues") || {};
        const finalUpdatedValues = { ...currentValues };
        
        Object.entries(currencyUpdates).forEach(([locValue, currency]) => {
          if (!finalUpdatedValues[locValue]) {
            finalUpdatedValues[locValue] = {};
          }
          finalUpdatedValues[locValue].currency = currency;
        });
        
        // Single update for all currency changes
        setValue("locationValues", finalUpdatedValues);
      }
    }
  };

  // Update the onSubmit function to properly handle catalog vs menu items
  const onSubmit = (data) => {
    // CRITICAL FIX: Only include locations that are actually selected
    const selectedLocationIds = (data.locations || []).map(locValue => {
      const locObj = locationsListOptions.find(loc => loc.value === locValue);
      return locObj ? locObj.id : locValue;
    });

    // Transform locationValues to locationStock using only selected locations
    // Now includes price and currency per location
    const locationStock = selectedLocationIds.map(locationId => {
      const locationValue = locationsListOptions.find(loc => loc.id === locationId)?.value;
      const values = data.locationValues[locationValue] || {};
      
      return {
        locationId: locationId,
        currency: values.currency || "USD",
        price: Number(values.price) || 0,
        sku: values.sku || "",
        stockOnHand: Number(values.stockOnHand) || 0,
        stockCommitted: Number(values.stockCommitted) || 0,
        awaitingNewStockDelivery: values.awaitingNewStockDelivery || "",
        awaitingNewStockCount: Number(values.awaitingNewStockCount) || 0,
      };
    });

    const {
      locations,
      locationValues,
      ...rest
    } = data;

    // For catalog items, calculate stockCount from location stock
    let processedData = { ...rest, locationStock };
    
    if (isCatalog) {
      // For catalog items, calculate stockCount from per-location stock
      const totalStock = locationStock.reduce(
        (total, loc) => total + (Number(loc.stockOnHand) || 0),
        0
      );
      processedData.stockCount = totalStock;
      processedData.stockOnHand = totalStock;
      
      // Stock information is now location-based, so no general fields needed
      
    } else {
      // For menu items, calculate stockOnHand from location stock
      const totalStock = locationStock.reduce(
        (total, loc) => total + (Number(loc.stockOnHand) || 0),
        0
      );
      processedData.stockOnHand = totalStock;
    }

    // Store variant number (the code will be generated when sending to backend)
    const variantNumber = (data.variantNumber || "").trim();
    processedData.variantNumber = variantNumber;

    if (onSave) onSave(processedData);
  };

  // Render location-specific fields dynamically (no validation)
  const renderLocationFields = () =>
    (selectedLocations || []).map((locValue) => {
      const locObj = locationsListOptions.find((loc) => loc.value === locValue);
      const locLabel = locObj?.label || locValue;
      const locationCurrency = locationValues?.[locValue]?.currency || "USD";
      return (
        <Box
          key={locValue}
          sx={{ mb: 2, border: "1px solid #eee", borderRadius: 2, p: 1 }}
        >
          <Typography sx={{ mb: 1 , fontWeight: 600 , color:'#000000'}}>{locLabel}</Typography>
          <Grid container spacing={1} alignItems="center">
            {locationFields.map((field) => (
              <React.Fragment key={field.name}>
                <Grid item xs={8}>
                  <Typography>{field.label}</Typography>
                </Grid>
                <Grid item xs={4}>
                  {field.name === "currency" ? (
                    <Controller
                      name={`locationValues.${locValue}.${field.name}`}
                      control={control}
                      defaultValue={locationCurrency}
                      render={({ field: innerField }) => {
                        const currencyValue = innerField.value || locationCurrency || "USD";
                        return (
                          <Autocomplete
                            options={currencies}
                            getOptionLabel={(option) => option.label || ""}
                            value={
                              currencies.find(
                                (currency) => currency.code === currencyValue
                              ) || currencies.find((c) => c.code === "USD") || currencies[0]
                            }
                            onChange={(event, newValue) => {
                              innerField.onChange(newValue ? newValue.code : "USD");
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="search currency..."
                                disabled
                              />
                            )}
                            disabled
                            fullWidth
                          />
                        );
                      }}
                    />
                  ) : field.name === "price" ? (
                    <Controller
                      name={`locationValues.${locValue}.${field.name}`}
                      control={control}
                      render={({ field: innerField }) => (
                        <TextField
                          {...innerField}
                          type="number"
                          fullWidth
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {currencies.find((c) => c.code === locationCurrency)?.symbol || "$"}
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  ) : (
                    <Controller
                      name={`locationValues.${locValue}.${field.name}`}
                      control={control}
                      render={({ field: innerField }) => (
                        <TextField
                          {...innerField}
                          type={field.type}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  )}
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Box>
      );
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ p: 2, pb: 0 }}>
        <Box display="flex" justifyContent="flex-end">
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, mt: 2 }}>
        <form style={{ marginTop: 4 }}>
          <Controller
            name="variant"
            control={control}
            disabled
            render={({ field }) => (
              <TextField
                {...field}
                label="variant type"
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              />
            )}
          />
          <Typography sx={{ mt: 1, mb: 1.5 }} fontSize={16}>
            variant description
          </Typography>
          <Controller
            name="variantDescription"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder=""
                fullWidth
                variant="outlined"
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />
            )}
          />
          
          <Typography sx={{ mt: 1, mb: 1.5 }} fontSize={16}>
            variant number
          </Typography>
          <Controller
            name="variantNumber"
            control={control}
            render={({ field }) => {
              return (
                <TextField
                  {...field}
                  placeholder="e.g., 01, 02, 03"
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 10 }}
                />
              );
            }}
          />

          {/* Show different fields based on isCatalog */}
          {isCatalog ? (
            // Catalog fields - stock information is now location-based
            <>
              {/* Location selection for catalog variants */}
              <Typography
                sx={{
                  fontWeight: "normal",
                  mb: 1,
                  mt: 2,
                  fontWeight: 600,
                  color: "#000000",
                }}
              >
                stock information
              </Typography>
              <Typography sx={{ mb: 3, fontWeight: "normal" }}>
                per location stock count
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>select location</InputLabel>
                <Controller
                  name="locations"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      multiple
                      label="select location"
                      onChange={async (e) => {
                        const selectedValues = e.target.value;
                        const previousValues = field.value || [];
                        
                        // Remove deselected locations from locationValues
                        const currentLocationValues = watch("locationValues") || {};
                        const updatedLocationValues = { ...currentLocationValues };
                        
                        // Find locations that were deselected
                        const deselected = previousValues.filter(
                          (loc) => !selectedValues.includes(loc)
                        );
                        
                        // Remove deselected locations from locationValues
                        deselected.forEach((locValue) => {
                          delete updatedLocationValues[locValue];
                        });
                        
                        // Update locationValues to remove deselected locations
                        setValue("locationValues", updatedLocationValues);
                        
                        field.onChange(selectedValues);
                        // Set default currency for newly selected locations
                        await setDefaultCurrencyForLocations(selectedValues);
                      }}
                      renderValue={(selected) =>
                        locationsListOptions
                          .filter((opt) => selected.includes(opt.value))
                          .map((opt) => opt.label)
                          .join(", ")
                      }
                    >
                      {locationsListOptions.map((loc) => (
                        <MenuItem key={loc.id} value={loc.value}>
                          <Checkbox
                            checked={field.value?.includes(loc.value) || false}
                          />
                          <ListItemText primary={loc.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              {renderLocationFields()}
            </>
          ) : (
            // Menu fields (nutritional information + location stock)
            <>
              <Typography
                sx={{
                  fontWeight: "normal",
                  mb: 1,
                  fontWeight: 600,
                  color: "#000000",
                }}
              >
                nutritional information
              </Typography>
              <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
                {[
                  "totalWeight",
                  "kcal",
                  "protein",
                  "carbs",
                  "fats",
                  "fibre",
                ].map((f, i) => (
                  <React.Fragment key={f}>
                    <Grid item xs={6}>
                      <Typography sx={{ fontWeight: "normal" }}>
                        {f === "carbs"
                          ? "carbohydrates"
                          : f.charAt(0).toLowerCase() + f.slice(1)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Controller
                        name={f}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Typography sx={{ fontWeight: "normal" }}>
                        {[
                          "totalWeight",
                          "protein",
                          "carbs",
                          "fats",
                          "fibre",
                        ].includes(f)
                          ? "g"
                          : f === "kcal"
                          ? "kcal"
                          : ""}
                      </Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
              <Typography
                sx={{
                  fontWeight: "normal",
                  mb: 1,
                  fontWeight: 600,
                  color: "#000000",
                }}
              >
                stock information
              </Typography>
              <Typography sx={{ mb: 3, fontWeight: "normal" }}>
                per location stock count
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>select location</InputLabel>
                <Controller
                  name="locations"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      multiple
                      label="select location"
                      onChange={async (e) => {
                        const selectedValues = e.target.value;
                        const previousValues = field.value || [];
                        
                        // Remove deselected locations from locationValues
                        const currentLocationValues = watch("locationValues") || {};
                        const updatedLocationValues = { ...currentLocationValues };
                        
                        // Find locations that were deselected
                        const deselected = previousValues.filter(
                          (loc) => !selectedValues.includes(loc)
                        );
                        
                        // Remove deselected locations from locationValues
                        deselected.forEach((locValue) => {
                          delete updatedLocationValues[locValue];
                        });
                        
                        // Update locationValues to remove deselected locations
                        setValue("locationValues", updatedLocationValues);
                        
                        field.onChange(selectedValues);
                        // Set default currency for newly selected locations
                        await setDefaultCurrencyForLocations(selectedValues);
                      }}
                      renderValue={(selected) =>
                        locationsListOptions
                          .filter((opt) => selected.includes(opt.value))
                          .map((opt) => opt.label)
                          .join(", ")
                      }
                    >
                      {locationsListOptions.map((loc) => (
                        <MenuItem key={loc.id} value={loc.value}>
                          <Checkbox
                            checked={field.value?.includes(loc.value) || false}
                          />
                          <ListItemText primary={loc.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              {renderLocationFields()}
            </>
          )}

          {/* Top-level qty removed from output */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              bgcolor: "#6c2bd7",
              mt: 3,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: 20,
              textTransform: "lowercase",
              py: 1,
              ":hover": { bgcolor: "#4e1fa2" },
            }}
            onClick={handleSubmit(onSubmit)}
          >
            Save
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
